import { Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useModel } from 'umi';
import { useHeadTeachers } from '@/hooks/useHeadTeachers';
import { getUsername } from '@/utils/utils';

const HeadTeacherSelector = ({
  onChange,
  onSelect,
  withDeleted = false,
  nullOption = false,
  allowCustomOption = false,
  defaultActiveCurrentUser = false,
  defaultActiveOnlyOption = true,
  excludeIds = [],
  purpose,
  selectedUser,
  selectedUsers,
  ...rest
}) => {
  const {
    initialState: { currentUser },
  } = useModel('@@initialState');
  const {
    data,
    isLoading: loading,
    isError,
  } = useHeadTeachers({
    withDeleted,
    ...(purpose ? { purpose } : {}),
  });

  // 保证页面行为统一，强制使用定制tags mode
  if (rest.mode === 'tags') {
    rest.mode = 'multiple';
    allowCustomOption = true;
  }

  const [options, setOptions] = useState([]);
  const initialized = useRef(false);
  const excludedIdsKey = JSON.stringify(excludeIds);

  useEffect(() => {
    if (!loading && data) {
      const options = (data || []).filter((el) => !excludeIds.some(id => Number(id) === Number(el.id))).map((el) => {
        let metadata = '';
        if (el.chineseName) {
          metadata += `${el.chineseName}-`;
        }
        if (el.firstName && el.lastName) {
          metadata += `${el.firstName.toLowerCase()}${el.lastName.toLowerCase()}-${el.lastName.toLowerCase()}${el.firstName.toLowerCase()}`;
        }
        return {
          key: el.id,
          value: el.id,
          label: getUsername(el),
          metadata: metadata,
        };
      });
      // 历史查询保留“无班主任”，未迁移的调用沿用原有条件。
      if (nullOption && purpose !== 'assignment' && (purpose === 'history' || options.length > 1)) {
        options.push({
          key: 0,
          value: 0,
          label: '无班主任',
        });
      }
      const hasValue = Array.isArray(rest.value) ? rest.value.length > 0 : rest.value != null;
      if (!initialized.current && !hasValue && purpose !== 'history') {
        const selectDefault = value => onChange?.(rest.mode === 'multiple' ? [value] : value);
        if (defaultActiveOnlyOption && options.length === 1) {
          selectDefault(options[0].value);
        } else if (defaultActiveCurrentUser && options.some((el) => el.value === currentUser.id)) {
          selectDefault(currentUser.id);
        }
      }
      // 只在首次载入时默认选择；清空、移除或刷新候选不能覆盖用户操作。
      initialized.current = true;
      if (purpose === 'assignment') {
        const values = (Array.isArray(rest.value) ? rest.value : [rest.value]).map(Number);
        [...(selectedUsers || []), ...(selectedUser ? [selectedUser] : [])].forEach(user => {
          if (user?.id && values.includes(Number(user.id)) && !options.some(el => el.value === user.id)) {
            options.push({
              key: user.id, value: user.id,
              label: `${getUsername(user)}（原班主任）`, disabled: rest.mode !== 'multiple',
            });
          }
        });
      }
      setOptions(options);
    }
    return () => {
      setOptions([]);
    };
  }, [loading, data, purpose, rest.value, rest.mode, selectedUser, selectedUsers, excludedIdsKey]);

  const matchWithInput = (input, option) => {
    if (!input || !option) return false;
    let match = false;
    if (!match && option.label) {
      match = option.label.indexOf(input) !== -1;
    }
    if (!match && option.metadata) {
      match = option.metadata.indexOf(input) !== -1;
    }
    return match;
  };

  const searchHandler = (input) => {
    if (!input || !allowCustomOption || purpose === 'assignment') {
      return;
    }
    if (options.some((el) => matchWithInput(input, el))) {
      return;
    }
    const newOptions = options.filter((el) => el.key !== -1);
    setOptions([
      ...newOptions,
      {
        key: -1,
        value: input,
        label: input,
        metadata: input,
      },
    ]);
  };

  return (
    <Select
      showSearch
      loading={loading}
      disabled={loading || isError}
      options={options}
      onSelect={(value) => {
        if (typeof onSelect === 'function') {
          onSelect(options.find((el) => el.value === value));
        }
      }}
      onChange={onChange}
      filterOption={matchWithInput}
      onSearch={searchHandler}
      placeholder="可用中文或拼音搜索姓名"
      {...rest}
    />
  );
};

export default HeadTeacherSelector;
